import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Trash2, CheckCircle, Archive, Inbox, Mail, Clock, User } from 'lucide-react';
import TridentIcon from '../components/TridentIcon';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import {
  adminLogin,
  fetchInquiries,
  updateInquiryStatus,
  deleteInquiry,
  getAdminToken,
  clearAdminToken,
} from '../lib/api';

const STATUS_OPTIONS = [
  { key: 'new', label: 'New', icon: Inbox, color: '#C8A45D' },
  { key: 'contacted', label: 'Contacted', icon: CheckCircle, color: '#8A6A35' },
  { key: 'archived', label: 'Archived', icon: Archive, color: '#B9A98A' },
];

const AdminLogin = ({ onSuccess }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(password);
      onSuccess();
    } catch (err) {
      toast({
        title: 'Access Denied',
        description: 'The password is incorrect.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080A0D] flex items-center justify-center px-6">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,#C8A45D08_0%,transparent_60%)]" />
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-12">
          <TridentIcon className="w-12 h-12 mx-auto mb-6" color="#C8A45D" />
          <p className="text-[#C8A45D] text-xs tracking-[0.3em] mb-4">IKARUS LANDED</p>
          <h1 className="text-4xl font-light text-[#F3E8D2] mb-3">The Archive</h1>
          <p className="text-[#B9A98A] italic text-sm">Private threshold of the house</p>
        </div>

        <Card className="bg-[#0B0F14] border-[#C8A45D]/20 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-[#B9A98A] text-xs tracking-widest mb-2 block">ACCESS KEY</label>
              <Input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[#080A0D] border-[#C8A45D]/20 text-[#F3E8D2] focus:border-[#C8A45D] transition-colors"
                placeholder="Enter password"
                data-testid="admin-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C8A45D] hover:bg-[#BFA05A] text-[#080A0D] py-6 tracking-wide transition-all duration-300 hover:shadow-lg hover:shadow-[#C8A45D]/20"
              data-testid="admin-login-btn"
            >
              {loading ? 'Verifying...' : 'Enter The Archive'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

const InquiryCard = ({ inquiry, onStatusChange, onDelete }) => {
  const statusConfig = STATUS_OPTIONS.find((s) => s.key === inquiry.status) || STATUS_OPTIONS[0];
  const StatusIcon = statusConfig.icon;

  return (
    <Card className="bg-[#0B0F14] border-[#C8A45D]/20 p-6 hover:border-[#C8A45D]/40 transition-all duration-300" data-testid={`inquiry-card-${inquiry.id}`}>
      <div className="flex items-start justify-between mb-4 gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-4 h-4 text-[#C8A45D]" />
            <h3 className="text-xl font-light text-[#F3E8D2]">{inquiry.name}</h3>
            {inquiry.founderAccess && (
              <span className="bg-[#C8A45D] text-[#080A0D] text-[10px] px-2 py-1 tracking-widest">
                FOUNDING
              </span>
            )}
          </div>
          <a
            href={`mailto:${inquiry.email}`}
            className="text-[#C8A45D] text-sm hover:underline flex items-center gap-2"
          >
            <Mail className="w-3 h-3" />
            {inquiry.email}
          </a>
        </div>
        <div className="flex items-center gap-2 text-xs text-[#B9A98A]">
          <Clock className="w-3 h-3" />
          {new Date(inquiry.timestamp).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </div>

      <div className="space-y-3 text-sm border-t border-[#C8A45D]/10 pt-4">
        {inquiry.interest && (
          <div>
            <p className="text-[#B9A98A] text-xs tracking-widest uppercase mb-1">Interested Piece</p>
            <p className="text-[#F3E8D2]">{inquiry.interest}</p>
          </div>
        )}
        {inquiry.timeline && (
          <div>
            <p className="text-[#B9A98A] text-xs tracking-widest uppercase mb-1">Occasion / Timeline</p>
            <p className="text-[#F3E8D2]">{inquiry.timeline}</p>
          </div>
        )}
        {inquiry.budgetComfort && (
          <div>
            <p className="text-[#B9A98A] text-xs tracking-widest uppercase mb-1">Estimated Commission Comfort</p>
            <p className="text-[#F3E8D2]">{inquiry.budgetComfort}</p>
          </div>
        )}
        {inquiry.message && (
          <div>
            <p className="text-[#B9A98A] text-xs tracking-widest uppercase mb-1">Message</p>
            <p className="text-[#F3E8D2] leading-relaxed">{inquiry.message}</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#C8A45D]/10">
        <div className="flex items-center gap-2">
          <StatusIcon className="w-4 h-4" style={{ color: statusConfig.color }} />
          <select
            value={inquiry.status}
            onChange={(e) => onStatusChange(inquiry.id, e.target.value)}
            className="bg-transparent text-[#F3E8D2] text-sm border border-[#C8A45D]/20 px-3 py-1 cursor-pointer focus:outline-none focus:border-[#C8A45D]"
            data-testid={`status-select-${inquiry.id}`}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.key} value={s.key} className="bg-[#0B0F14]">
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(inquiry.id)}
          className="text-[#B9A98A] hover:text-red-400 hover:bg-red-400/10"
          data-testid={`delete-btn-${inquiry.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
};

const AdminDashboard = ({ onLogout }) => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { toast } = useToast();

  const loadInquiries = async () => {
    setLoading(true);
    try {
      const data = await fetchInquiries();
      setInquiries(data);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
      } else {
        toast({ title: 'Failed to load inquiries', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInquiries();
  }, []);

  const handleStatusChange = async (id, status) => {
    try {
      const updated = await updateInquiryStatus(id, status);
      setInquiries((prev) => prev.map((i) => (i.id === id ? updated : i)));
      toast({ title: 'Status updated' });
    } catch (err) {
      toast({ title: 'Failed to update', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this inquiry permanently?')) return;
    try {
      await deleteInquiry(id);
      setInquiries((prev) => prev.filter((i) => i.id !== id));
      toast({ title: 'Inquiry deleted' });
    } catch (err) {
      toast({ title: 'Failed to delete', variant: 'destructive' });
    }
  };

  const filtered = filter === 'all' ? inquiries : inquiries.filter((i) => i.status === filter);
  const counts = {
    all: inquiries.length,
    new: inquiries.filter((i) => i.status === 'new').length,
    contacted: inquiries.filter((i) => i.status === 'contacted').length,
    archived: inquiries.filter((i) => i.status === 'archived').length,
  };

  return (
    <div className="min-h-screen bg-[#080A0D]">
      {/* Header */}
      <nav className="border-b border-[#C8A45D]/20 bg-[#0B0F14]/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TridentIcon className="w-6 h-6" color="#C8A45D" />
            <div>
              <p className="text-[#C8A45D] text-[10px] tracking-[0.3em]">IKARUS LANDED</p>
              <p className="text-[#F3E8D2] text-sm tracking-wider">The Archive</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={onLogout}
            className="text-[#B9A98A] hover:text-[#C8A45D]"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Exit
          </Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Stats + Filter */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { key: 'all', label: 'All Inquiries', count: counts.all },
            { key: 'new', label: 'New', count: counts.new },
            { key: 'contacted', label: 'Contacted', count: counts.contacted },
            { key: 'archived', label: 'Archived', count: counts.archived },
          ].map((stat) => (
            <button
              key={stat.key}
              onClick={() => setFilter(stat.key)}
              className={`p-6 border text-left transition-all duration-300 ${
                filter === stat.key
                  ? 'border-[#C8A45D] bg-[#C8A45D]/5'
                  : 'border-[#C8A45D]/20 bg-[#0B0F14] hover:border-[#C8A45D]/40'
              }`}
              data-testid={`filter-${stat.key}`}
            >
              <p className="text-[#B9A98A] text-xs tracking-widest uppercase mb-2">{stat.label}</p>
              <p className="text-3xl font-light text-[#F3E8D2]">{stat.count}</p>
            </button>
          ))}
        </div>

        {/* Inquiries list */}
        {loading ? (
          <div className="text-center py-20 text-[#B9A98A]">Loading the archive...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <TridentIcon className="w-12 h-12 mx-auto mb-4 opacity-30" color="#C8A45D" />
            <p className="text-[#B9A98A] italic">
              {inquiries.length === 0
                ? 'The archive awaits its first collector.'
                : 'No inquiries match this filter.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="inquiries-list">
            {filtered.map((inquiry) => (
              <InquiryCard
                key={inquiry.id}
                inquiry={inquiry}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [isAuth, setIsAuth] = useState(!!getAdminToken());

  const handleLogout = () => {
    clearAdminToken();
    setIsAuth(false);
  };

  if (!isAuth) {
    return <AdminLogin onSuccess={() => setIsAuth(true)} />;
  }
  return <AdminDashboard onLogout={handleLogout} />;
};

export default AdminPage;
